import React from 'react';
import { Redirect } from 'react-router';

import { Query } from '@apollo/client/react/components';
import { Button, Loader } from '@opendoor/bricks/core';
import ContentWrapper from '@opendoor/bricks/deprecated/UNSAFE_layout/ContentWrapper';

import {
  ALL,
  DropdownMultiSelect,
  DropdownSelect,
  OptionType,
} from '../../components/DropdownSelects';
import Layout from '../../components/Layout';
import { DATE_OPTIONS, MARKET_OPTIONS } from '../../config/reports';
import { GET_REPORTS, IGetReports, IGetReportsVariables } from '../../graphql/queries/GetReports';
import { datesToParams, optionToDates } from '../../helpers/formatUtils';
import { FilterBar, FilterTitle } from '../../styles/components';
import { Location } from '../../types';
import { ERROR_PAGE_ROUTE, REPORTING_DASHBOARD_ROUTE } from '../routes';

type FullReportState = {
  reportId?: string;
  markets: Array<string>;
  startDate?: string;
  endDate?: string;
  dataReceived?: boolean;
  data?: IGetReports;
  dateFrame: string;
  waitingForData: boolean;
};

class FullReport extends React.Component<{ location: Location }, FullReportState> {
  constructor(props: { location: Location; match: { params: { reportId: string } } }) {
    super(props);
    const { startDate, endDate } = datesToParams(optionToDates(DATE_OPTIONS[0].value));
    this.state = {
      markets: MARKET_OPTIONS.map((d) => d.value),
      startDate,
      endDate,
      reportId: props.match.params.reportId,
      dateFrame: DATE_OPTIONS[0].value,
      waitingForData: true,
    };
  }

  private downloadRef: React.RefObject<HTMLAnchorElement> = React.createRef<HTMLAnchorElement>();

  public onmessage = (event: any) => {
    if (event.origin === 'https://app.mode.com' && event.data.type === 'reportExportPaths') {
      const modeBaseUrl = event.origin;
      const csvExportUrl = modeBaseUrl + event.data.report_csv_export_path;
      if (this.downloadRef.current) {
        this.downloadRef.current.setAttribute('href', csvExportUrl);
        this.setState({ waitingForData: false });
      }
    }
  };

  public updateMarket = (values: Array<OptionType> | undefined) => {
    if (values && values.length) {
      const markets = values.filter((v) => v.value !== ALL.value).map((v) => v.value);
      this.setState({
        markets,
      });
    }
  };

  public updateDateFrame = (value: OptionType | undefined) => {
    if (value && value.value !== this.state.dateFrame) {
      const { startDate, endDate } = datesToParams(optionToDates(value.value));
      this.setState({
        startDate,
        endDate,
      });
    }
  };

  public selectionSummary = (values: Array<OptionType> | undefined) => {
    if (!values) {
      return 'none';
    }
    const valueValues = values.map((v) => v.value);
    if (valueValues.indexOf(ALL.value) !== -1) {
      return 'All Markets';
    }

    return valueValues.length + ' Markets';
  };

  public componentDidMount() {
    window.addEventListener('message', this.onmessage);
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.onmessage);
  }

  public render() {
    return (
      <Layout
        renderNav={this.renderNav(this.state.waitingForData)}
        renderContent={this.renderContent}
      />
    );
  }

  private renderNav = (waitingForData: boolean) => {
    // eslint-disable-next-line react/display-name
    return () => (
      <div style={{ marginTop: '76px' }}>
        {/* eslint-disable-next-line */}
        <a ref={this.downloadRef} id={`csv-download-${this.state.reportId}`} target="_blank">
          {waitingForData ? (
            <Loader />
          ) : (
            <Button
              /** TODO @partnerships Semantic buttons without onClick methods are a11y violations. See https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1943339236/Accessibility+best+practices+for+frontend+development#Adding-onClick-handlers-to-non-interactive-elements-such-as-%3Cdiv-/%3E-or-%3CBox-/%3E-not-started */
              onClick={() => {}}
              aria-label=""
              /** @ts-expect-error TODO @partnerships Remove border radius override */
              borderRadius="rounded"
              variant="primary"
            >
              Download as csv
            </Button>
          )}
        </a>
      </div>
    );
  };

  private renderContent = () => {
    return (
      <ContentWrapper>
        <FilterBar>
          <FilterTitle>Filter:</FilterTitle>
          <DropdownMultiSelect
            aria-labelledby="market"
            options={MARKET_OPTIONS}
            includeAllOption={true}
            defaultValues={MARKET_OPTIONS}
            onChange={this.updateMarket}
            selectionSummary={this.selectionSummary}
          />
          <DropdownSelect
            options={DATE_OPTIONS}
            defaultValue={DATE_OPTIONS[0]}
            onChange={this.updateDateFrame}
          />
        </FilterBar>
        <Query<IGetReports, IGetReportsVariables>
          query={GET_REPORTS}
          variables={{
            marketNames: this.state.markets,
            startDate: this.state.startDate,
            endDate: this.state.endDate,
            reportId: this.state.reportId,
          }}
          fetchPolicy="network-only"
        >
          {({ loading, error, data }) => {
            if (error) {
              return <Redirect to={ERROR_PAGE_ROUTE} />;
            }
            if (loading) {
              return <Loader />;
            }
            if (!data) {
              return <Redirect to={REPORTING_DASHBOARD_ROUTE} />;
            }

            return (
              <iframe
                style={{ height: 'calc(100vh - 240px)' }}
                id={`iframe-${this.state.reportId}`}
                title={`iframe-${this.state.reportId}`}
                src={data.reports[0].url}
                width="100%"
                frameBorder="0"
              />
            );
          }}
        </Query>
      </ContentWrapper>
    );
  };
}

export default FullReport;
